/**
 * A label as Linear draws one: a pill with a coloured dot and the name, the
 * colour fixed per label so "Jersey" is the same green everywhere, and a
 * small chevron when the chip opens something.
 *
 * @module components/ui/label-chip
 */
import { ChevronDownIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

import { avatarHues } from "~/lib/avatarGradient";
import { cn } from "~/lib/utils";

/** A stable, saturated dot colour for a label name. */
export function labelDotColor(name: string): string {
  const [hue] = avatarHues(name);
  return `oklch(0.66 0.17 ${hue})`;
}

export function LabelChip({
  name,
  color,
  chevron = false,
  className,
  ...props
}: ComponentProps<"button"> & {
  readonly name: string;
  /** Override the hashed colour, e.g. a severity's own red. */
  readonly color?: string;
  readonly chevron?: boolean;
}) {
  return (
    <button
      type="button"
      data-slot="label-chip"
      className={cn(
        "inline-flex h-6 max-w-full cursor-pointer items-center gap-1.5 rounded-full border border-border bg-background px-2 text-xs font-medium text-foreground/85 outline-hidden transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-background",
        className,
      )}
      {...props}
    >
      <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color ?? labelDotColor(name) }} />
      <span className="truncate">{name}</span>
      {chevron ? <ChevronDownIcon aria-hidden className="-mr-0.5 size-3 shrink-0 text-muted-foreground" /> : null}
    </button>
  );
}

/**
 * The same label, drawn as Linear draws it inside a list row: a static pill
 * (the row itself is the control), muted text, a 1px border, and either the
 * label's dot or a small icon of its own.
 */
export function LabelTag({
  name,
  color,
  icon,
  className,
}: {
  readonly name: string;
  /** Override the hashed dot colour, e.g. a severity's own red. */
  readonly color?: string;
  /** Stands in for the dot, e.g. an issuer's mark. */
  readonly icon?: ReactNode;
  readonly className?: string;
}) {
  return (
    <span
      data-slot="label-tag"
      title={name}
      className={cn(
        "inline-flex h-5 min-w-0 max-w-full shrink-0 items-center gap-1 rounded-full border border-border/80 pe-1.5 ps-1 text-[11px] text-muted-foreground",
        className,
      )}
    >
      {icon ?? (
        <span aria-hidden className="mx-[3px] size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color ?? labelDotColor(name) }} />
      )}
      <span className="truncate">{name}</span>
    </span>
  );
}
